// src/pages/Appli/AsideMenu.jsx

import React from "react";

import {
  Link,
  useLocation,
} from "react-router-dom";

import {
  FiMessageCircle,
  FiPhoneCall,
  FiUsers,
} from "react-icons/fi";

import {
  useAsideMenu,
} from "./../../contexts/AsideMenuContext";

import useDashboardAlerts from "../../hooks/useDashboardAlerts";

import {
  useVisitorAdminAlertsContext,
} from "./AppliAdmin/VisitorCenter/context/VisitorAdminAlertsContext";

import {
  DASHBOARD_BRAND,
  buildDashboardPath,
  getMenusForRole,
  isDashboardPathActive,
} from "./dashboardMenuConfig";

import "./AsideMenu.css";

const cn = (...classes) =>
  classes
    .filter(Boolean)
    .join(" ");

function formatBadgeCount(
  value,
) {
  const count =
    Number(value || 0);

  if (count <= 0) {
    return null;
  }

  if (count > 99) {
    return "99+";
  }

  return count;
}

const AsideMenu = ({
  role_id,
}) => {
  const {
    isOpen,
    toggleMenu,
  } = useAsideMenu();

  const location =
    useLocation();

  /* ==========================================================================
   * ALERTES HISTORIQUES DU DASHBOARD
   * ========================================================================== */

  const {
    badgeMap:
      dashboardBadgeMap,

    subscriptionAlerts,
  } =
    useDashboardAlerts({
      enabled:
        Number(role_id) ===
        1,
    });

  /* ==========================================================================
   * CENTRE VISITEURS
   * ========================================================================== */

  const {
    summary:
      visitorSummary,
  } =
    useVisitorAdminAlertsContext();

  const unreadConversations =
    Number(
      visitorSummary
        ?.unread_conversations ||
        0,
    );

  const newCallbackRequests =
    Number(
      visitorSummary
        ?.new_callback_requests ||
        0,
    );

  const liveVisitors =
    Number(
      visitorSummary
        ?.live_visitors ||
        0,
    );

  /*
   * On recalcule le total
   * côté front afin de
   * rester fiable même si
   * total_alerts est absent.
   */
  const visitorAlertsCount =
    unreadConversations +
    newCallbackRequests;

  /* ==========================================================================
   * FUSION DES BADGES
   * ========================================================================== */

  const badgeMap = {
    ...(dashboardBadgeMap ||
      {}),

    visitorAlerts:
      visitorAlertsCount,
  };

  const currentMenus =
    getMenusForRole(
      role_id,
    );

  const BrandIcon =
    DASHBOARD_BRAND.icon;

  const hasCriticalSubscriptionAlerts =
    Number(
      subscriptionAlerts
        ?.critical || 0,
    ) > 0;

  /* ==========================================================================
   * RENDER
   * ========================================================================== */

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-[10000] h-screen border-r border-slate-200/80 bg-[#F7FAFC] shadow-[8px_0_30px_rgba(15,23,42,0.04)] transition-all duration-300",

        isOpen
          ? "w-[290px]"
          : "w-[92px]",
      )}
    >
      <div className="flex h-full flex-col">
        {/* ================================================================
         * EN-TÊTE
         * ============================================================== */}

        <div
          className={cn(
            "border-b border-slate-200/80 px-3 py-4",

            isOpen
              ? "flex items-center justify-between gap-3"
              : "flex flex-col items-center gap-3",
          )}
        >
          <Link
            to="/"
            className={cn(
              "min-w-0",

              !isOpen &&
                "flex justify-center",
            )}
          >
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#2EA7E0] text-white shadow-sm shadow-sky-200">
                <BrandIcon className="text-[24px]" />
              </div>

              {isOpen && (
                <div className="min-w-0">
                  <div className="truncate text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">
                    {
                      DASHBOARD_BRAND.name
                    }
                  </div>

                  <div className="truncate text-[22px] font-bold leading-none text-[#123B57]">
                    {
                      DASHBOARD_BRAND.title
                    }
                  </div>
                </div>
              )}
            </div>
          </Link>

          <button
            type="button"
            onClick={
              toggleMenu
            }
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-[#123B57]"
            aria-label={
              isOpen
                ? "Réduire le menu"
                : "Ouvrir le menu"
            }
          >
            <span
              className={cn(
                "text-2xl leading-none transition-transform duration-300",

                !isOpen &&
                  "rotate-180",
              )}
            >
              ‹
            </span>
          </button>
        </div>

        {/* ================================================================
         * NAVIGATION
         * ============================================================== */}

        <div className="hide-scrollbar flex-1 overflow-y-auto px-3 py-4">
          {isOpen && (
            <div className="mb-3 px-2 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
              Navigation
            </div>
          )}

          <nav>
            <ul className="space-y-1.5">
              {currentMenus.map(
                (menu) => {
                  const Icon =
                    menu.icon;

                  const isActive =
                    isDashboardPathActive(
                      location.pathname,
                      menu.path,
                    );

                  const badgeValue =
                    formatBadgeCount(
                      menu.badgeKey
                        ? badgeMap?.[
                            menu.badgeKey
                          ]
                        : null,
                    );

                  const isVisitorMenu =
                    menu.path ===
                    "visiteurs";

                  const isSubscriptionMenu =
                    menu.path ===
                    "subscriptions";

                  /*
                   * Abonnements :
                   *
                   * - rouge si critique ;
                   * - orange sinon.
                   *
                   * Les autres badges
                   * conservent le rouge
                   * historique.
                   */
                  const badgeClassName =
                    isSubscriptionMenu &&
                    !hasCriticalSubscriptionAlerts
                      ? "bg-amber-500"
                      : "bg-[#F26B5E]";

                  const subscriptionBadgeTitle =
                    isSubscriptionMenu
                      ? [
                          `${Number(
                            subscriptionAlerts
                              ?.total ||
                              0,
                          )} action(s) à traiter`,

                          `${Number(
                            subscriptionAlerts
                              ?.renewals_to_prepare ||
                              0,
                          )} renouvellement(s) à préparer`,

                          `${Number(
                            subscriptionAlerts
                              ?.quotes_to_follow_up ||
                              0,
                          )} devis à relancer`,

                          `${Number(
                            subscriptionAlerts
                              ?.overdue_payments ||
                              0,
                          )} paiement(s) en retard`,
                        ].join(
                          " · ",
                        )
                      : undefined;

                  return (
                    <li
                      key={
                        menu.path
                      }
                    >
                      <Link
                        to={buildDashboardPath(
                          menu.path,
                        )}
                        title={
                          !isOpen
                            ? menu.title
                            : undefined
                        }
                        className={cn(
                          "group flex items-center rounded-2xl px-3 py-3 text-sm font-medium transition-all duration-200",

                          isOpen
                            ? "gap-3"
                            : "justify-center",

                          isActive
                            ? "bg-[#123B57] text-white shadow-[0_14px_34px_rgba(18,59,87,0.20)]"
                            : "text-slate-700 hover:bg-white hover:shadow-sm",
                        )}
                      >
                        {/* ==================================================
                         * ICÔNE
                         * ================================================ */}

                        <div
                          className={cn(
                            "relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border text-[20px] transition-all duration-200",

                            isActive
                              ? "border-white/10 bg-white/10 text-white"
                              : "border-slate-200 bg-white text-slate-500 group-hover:text-[#123B57]",
                          )}
                        >
                          <Icon />

                          {badgeValue && (
                            <span
                              title={
                                subscriptionBadgeTitle
                              }
                              className={cn(
                                "absolute -right-1.5 -top-1.5 flex min-h-[20px] min-w-[20px] items-center justify-center rounded-full border-2 border-[#F7FAFC] px-1 text-[10px] font-extrabold leading-none text-white shadow-sm",

                                badgeClassName,
                              )}
                            >
                              {
                                badgeValue
                              }
                            </span>
                          )}

                          {/*
                           * Petit indicateur vert
                           * lorsqu'un visiteur est
                           * actuellement en ligne.
                           */}

                          {isVisitorMenu &&
                            liveVisitors >
                              0 && (
                              <span
                                title={`${liveVisitors} visiteur(s) en ligne`}
                                className="absolute -bottom-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 border-[#F7FAFC] bg-emerald-500"
                              />
                            )}
                        </div>

                        {/* ==================================================
                         * TITRE / DÉTAILS
                         * ================================================ */}

                        {isOpen && (
                          <>
                            <span className="min-w-0 flex-1 truncate text-left text-[15px]">
                              {
                                menu.title
                              }
                            </span>

                            {/* ============================================
                             * VISITEURS
                             * ========================================== */}

                            {isVisitorMenu && (
                              <div className="flex shrink-0 items-center gap-1">
                                {unreadConversations >
                                  0 && (
                                  <span
                                    title={`${unreadConversations} conversation(s) non lue(s)`}
                                    className={cn(
                                      "inline-flex h-7 min-w-7 items-center justify-center gap-1 rounded-lg px-1.5 text-[11px] font-extrabold",

                                      isActive
                                        ? "bg-white/15 text-white"
                                        : "bg-red-50 text-red-600",
                                    )}
                                  >
                                    <FiMessageCircle />

                                    {formatBadgeCount(
                                      unreadConversations,
                                    )}
                                  </span>
                                )}

                                {newCallbackRequests >
                                  0 && (
                                  <span
                                    title={`${newCallbackRequests} demande(s) de rappel à traiter`}
                                    className={cn(
                                      "inline-flex h-7 min-w-7 items-center justify-center gap-1 rounded-lg px-1.5 text-[11px] font-extrabold",

                                      isActive
                                        ? "bg-white/15 text-white"
                                        : "bg-amber-50 text-amber-700",
                                    )}
                                  >
                                    <FiPhoneCall />

                                    {formatBadgeCount(
                                      newCallbackRequests,
                                    )}
                                  </span>
                                )}

                                {liveVisitors >
                                  0 &&
                                  unreadConversations ===
                                    0 &&
                                  newCallbackRequests ===
                                    0 && (
                                    <span
                                      title={`${liveVisitors} visiteur(s) en ligne`}
                                      className={cn(
                                        "inline-flex h-7 min-w-7 items-center justify-center gap-1 rounded-lg px-1.5 text-[11px] font-extrabold",

                                        isActive
                                          ? "bg-white/15 text-white"
                                          : "bg-emerald-50 text-emerald-700",
                                      )}
                                    >
                                      <FiUsers />

                                      {formatBadgeCount(
                                        liveVisitors,
                                      )}
                                    </span>
                                  )}
                              </div>
                            )}

                            {/* ============================================
                             * ABONNEMENTS
                             *
                             * Petit compteur supplémentaire
                             * lorsque le menu est ouvert.
                             * ========================================== */}

                            {isSubscriptionMenu &&
                              badgeValue && (
                                <span
                                  title={
                                    subscriptionBadgeTitle
                                  }
                                  className={cn(
                                    "inline-flex h-7 min-w-7 shrink-0 items-center justify-center rounded-lg px-2 text-[11px] font-extrabold",

                                    isActive
                                      ? "bg-white/15 text-white"
                                      : hasCriticalSubscriptionAlerts
                                        ? "bg-red-50 text-red-600"
                                        : "bg-amber-50 text-amber-700",
                                  )}
                                >
                                  {
                                    badgeValue
                                  }
                                </span>
                              )}
                          </>
                        )}
                      </Link>
                    </li>
                  );
                },
              )}
            </ul>
          </nav>
        </div>
      </div>
    </aside>
  );
};

export default AsideMenu;